import { useCallback, useEffect, useState } from "react";

const RERENDER_CLASSES = ['nav-link', 'page-link', 'action-link'];

/**
 *
 * @param beforeLeaving callback function to notify that user is leaving
 * @param safeZones element IDs of ignored zones
 */
export default function useBeforeLeaving(isSafeToLeave: () => boolean, beforeLeaving: () => void): {
  continueToLeave: () => void
} {
  const [leaving, setLeaving] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<EventTarget>();

  const onLeaving = useCallback((event: MouseEvent | KeyboardEvent | BeforeUnloadEvent) => {
    setLeaving(false);

    if (isSafeToLeave()) {
      return;
    }

    // noop if the keystroke is not for clicking
    if (event instanceof KeyboardEvent
      && event.key !== 'Enter'
      && event.key !== ' ') {
      return;
    }

    const targetElement = event.target as Element;
    const parentElement = targetElement.parentNode as Element;

    // skip if it's window unloading event
    if (!(event instanceof BeforeUnloadEvent)) {
      // check if the target will trigger a data rerender
      if (RERENDER_CLASSES.some(c => targetElement.classList?.contains(c)
        || parentElement.classList?.contains(c))) {
        // suspend the target, in case user chooses continueToLeave
        setPendingTarget(event.target ?? undefined);
        // notify caller that user is leaving
        setLeaving(true);
      } else {
        return; // noop
      }
    }

    event.preventDefault();
  }, [isSafeToLeave, setLeaving]);

  useEffect(() => {
    document.addEventListener("mousedown", onLeaving);
    document.addEventListener("keydown", onLeaving);
    window.addEventListener("beforeunload", onLeaving);

    return () => {
      document.removeEventListener("mousedown", onLeaving);
      document.removeEventListener("keydown", onLeaving);
      window.removeEventListener("beforeunload", onLeaving);
    };
  }, [onLeaving]);

  useEffect(() => {
    if (leaving) {
      beforeLeaving();
    }
  }, [leaving]);

  const continueToLeave = useCallback(() => {
    mouseClick(pendingTarget);
    setLeaving(false);
  }, [pendingTarget]);

  return { continueToLeave };
}

function mouseClick(element?: EventTarget) {
  ['mousedown', 'click', 'mouseup'].forEach(eventType =>
    element?.dispatchEvent(new MouseEvent(eventType, {
      view: window,
      bubbles: true,
      cancelable: true,
      buttons: 1
    })
    ));
}
